import { APP_CONFIG } from '@/config';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, any>;
  error?: Error;
}

const SENSITIVE_FIELDS = ['password', 'token', 'key', 'secret', 'cvv', 'email', 'phone'];

const redact = (data: any): any => {
  if (!data || typeof data !== 'object') return data;
  if (Array.isArray(data)) return data.map(redact);

  const redacted: any = {};
  for (const [key, value] of Object.entries(data)) {
    const isSensitive = SENSITIVE_FIELDS.some(f => key.toLowerCase().includes(f));
    redacted[key] = isSensitive ? '[REDACTED]' : redact(value);
  }
  return redacted;
};

class Logger {
  private get logLevelIndex(): number {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const envLevel = (APP_CONFIG.logLevel as LogLevel) || 'info';
    return levels.indexOf(envLevel);
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentIdx = levels.indexOf(level);
    if (APP_CONFIG.isProduction && level === 'debug') return false;
    return currentIdx >= this.logLevelIndex;
  }

  private format(entry: LogEntry): string {
    const base = `[${entry.timestamp}] [${entry.level.toUpperCase()}] ${entry.message}`;
    if (entry.context && Object.keys(entry.context).length > 0) {
      return `${base} | Context: ${JSON.stringify(redact(entry.context))}`;
    }
    return base;
  }

  debug(message: string, context?: Record<string, any>) {
    if (!this.shouldLog('debug')) return;
    // eslint-disable-next-line no-console
    console.debug(
      `%c${this.format(this.createEntry('debug', message, context))}`,
      'color: #9e9e9e'
    );
  }

  info(message: string, context?: Record<string, any>) {
    if (!this.shouldLog('info')) return;
    console.info(`%c${this.format(this.createEntry('info', message, context))}`, 'color: #2196f3');
  }

  warn(message: string, context?: Record<string, any>) {
    if (!this.shouldLog('warn')) return;
    console.warn(`%c${this.format(this.createEntry('warn', message, context))}`, 'color: #ff9800');
  }

  error(message: string, context?: Record<string, any>, error?: Error) {
    if (!this.shouldLog('error')) return;
    const entry = this.createEntry('error', message, context, error);
    console.error(`%c${this.format(entry)}`, 'color: #f44336', error || '');
    if (APP_CONFIG.isProduction) {
      // Future: External error tracking (Sentry/LogRocket)
    }
  }

  private createEntry(
    level: LogLevel,
    message: string,
    context?: Record<string, any>,
    error?: Error
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      error,
    };
  }
}

export const logger = new Logger();
export default logger;
